import { supabase } from '../lib/supabase';
import type { QueueInsightRefreshResult } from '../types/insights.types';

const RATE_LIMIT_SECONDS = 30;

/**
 * Run insight generation immediately for the current user (calls Edge Function ai-insights-refresh).
 * Use after queueInsightRefresh so the user sees new insights without waiting for the job worker.
 */
export async function runInsightRefreshImmediate(): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data, error } = await (supabase as any).functions.invoke('ai-insights-refresh', {
      body: {},
    });
    if (error) return { ok: false, error: error.message ?? String(error) };
    if (data?.error) return { ok: false, error: data.error };
    return { ok: true };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    return { ok: false, error: message };
  }
}

/**
 * Queue an insight refresh for the user. Returns { queued: true } or
 * { queued: false, reason: 'rate_limited' } when user has triggered refresh within the last 30 seconds.
 */
export async function queueInsightRefresh(userId: string): Promise<QueueInsightRefreshResult> {
  try {
    const { data: settings } = await (supabase as any)
      .from('user_ai_settings')
      .select('insight_refresh_queued_at')
      .eq('user_id', userId)
      .maybeSingle();

    const queuedAt = (settings as { insight_refresh_queued_at?: string | null } | null)?.insight_refresh_queued_at;
    if (queuedAt) {
      const elapsedMs = Date.now() - new Date(queuedAt).getTime();
      if (elapsedMs < RATE_LIMIT_SECONDS * 1000) {
        return { queued: false, reason: 'rate_limited' };
      }
    }

    const { error } = await (supabase as any).rpc('queue_insight_refresh_for_user', { p_user_id: userId });
    if (error) {
      if (error.code === '429' || error.message?.toLowerCase().includes('rate')) {
        return { queued: false, reason: 'rate_limited' };
      }
      console.warn('[queueInsightRefresh] RPC error:', error);
      return { queued: false, error: error.message ?? 'Could not queue refresh.' };
    }
    return { queued: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.warn('[queueInsightRefresh] Error:', message);
    return { queued: false, error: message };
  }
}

/**
 * @deprecated Use queueInsightRefresh which returns result shape. Kept for backward compatibility.
 */
export async function queueInsightsRefresh(userId: string): Promise<QueueInsightRefreshResult> {
  return queueInsightRefresh(userId);
}

